import {
  deleteObject,
  getDownloadURL,
  listAll,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { useCallback, useEffect, useState } from "react";
import { storage } from "../../firebase-config";

export type StorageImage = {
  name: string;
  url: string;
  fullPath: string;
  source?: "storage" | "configured";
};

export function useStorageImages(
  companyId?: string,
  folder: string = "cartas"
) {
  const [images, setImages] = useState<StorageImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  const getBaseRef = () => ref(storage, `${folder}/${companyId}`);

  const fetchImages = useCallback(async () => {
    if (!companyId) {
      setImages([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const folderRef = getBaseRef();
      const res = await listAll(folderRef);
      const urls = await Promise.all(
        res.items.map(async (itemRef) => ({
          name: itemRef.name,
          url: await getDownloadURL(itemRef),
          fullPath: itemRef.fullPath,
        }))
      );
      setImages(urls);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId, folder]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const uploadImages = async (files: File[]): Promise<StorageImage[]> => {
    const uploadedImages: StorageImage[] = [];
    setLoading(true);
    setError(null);
    try {
      for (const file of files) {
        const fileRef = ref(
          storage,
          `${folder}/${companyId}/${Date.now()}_${file.name.replace(
            /\s/g,
            "_"
          )}`
        );
        await new Promise<void>((resolve, reject) => {
          const uploadTask = uploadBytesResumable(fileRef, file);
          uploadTask.on(
            "state_changed",
            (snap) => {
              setProgress((snap.bytesTransferred / snap.totalBytes) * 100);
            },
            (err) => {
              setError(err.message);
              reject(err);
            },
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              const uploadedImage = {
                name: fileRef.name,
                url,
                fullPath: fileRef.fullPath,
                source: "storage" as const,
              };
              uploadedImages.push(uploadedImage);
              setImages((old) => [...old, uploadedImage]);
              setProgress(0);
              resolve();
            }
          );
        });
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    return uploadedImages;
  };

  const deleteImage = async (fullPath: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const fileRef = ref(storage, fullPath);
      await deleteObject(fileRef);
      setImages((imgs) => imgs.filter((img) => img.fullPath !== fullPath));
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const uploadFileAndGetUrl = async (
    file: File,
    phoneNumber: string,
    type: "image" | "audio" | "video" | "document"
  ): Promise<string> => {
    setLoading(true);
    setError(null);
    try {
      const fileRef = ref(
        storage,
        `chats/${phoneNumber}/${type}/${Date.now()}_${file.name.replace(
          /\s/g,
          "_"
        )}`
      );

      await new Promise<void>((resolve, reject) => {
        const uploadTask = uploadBytesResumable(fileRef, file);
        uploadTask.on(
          "state_changed",
          (snap) => {
            setProgress((snap.bytesTransferred / snap.totalBytes) * 100);
          },
          (err) => {
            setError(err.message);
            reject(err);
          },
          () => resolve()
        );
      });

      const url = await getDownloadURL(fileRef);
      setProgress(0);
      return url;
    } catch (e: any) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const uploadAssetAndGetUrl = async (
    file: File,
    assetFolder: string = "store-assets"
  ): Promise<string> => {
    if (!companyId) throw new Error("No hay compañía activa para subir el archivo.");
    setLoading(true);
    setError(null);
    try {
      const fileRef = ref(
        storage,
        `${assetFolder}/${companyId}/${Date.now()}_${file.name.replace(/\s/g, "_")}`
      );

      await new Promise<void>((resolve, reject) => {
        const uploadTask = uploadBytesResumable(fileRef, file);
        uploadTask.on(
          "state_changed",
          (snap) => {
            setProgress((snap.bytesTransferred / snap.totalBytes) * 100);
          },
          (err) => {
            setError(err.message);
            reject(err);
          },
          () => resolve()
        );
      });

      const url = await getDownloadURL(fileRef);
      setProgress(0);
      return url;
    } catch (e: any) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return {
    images,
    loading,
    error,
    progress,
    uploadImages,
    deleteImage,
    refresh: fetchImages,
    setImages,
    uploadFileAndGetUrl,
    uploadAssetAndGetUrl,
  };
}
